// src/App.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ScrollToTop from './components/ScrollToTop';
import ScrollTopButton from './components/ScrollTopButton'; // ✅ ページ先頭へ戻るボタン
// import WeChatGroupFloat from './components/WeChatGroupFloat'; // ⛔️ WeChat フロートを非表示
// import ChatBotWidget from './components/ChatBotWidget';   // ⛔️ 一時的にチャットウィジェットを非表示
import AppRoutes from './router';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { useTranslation } from 'react-i18next';
import GlobalFixes from './components/GlobalFixes'; // ✅ グローバルな表示不具合の修正コンポーネント
import ErrorBoundary from './components/ErrorBoundary';

/** <html> の lang / translate 属性を同期し、ブラウザや拡張機能の自動翻訳を抑制する */
const HtmlLangSync: React.FC = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const lang = i18n.language === 'zh' ? 'zh-CN' : 'ja';
    const root = document.documentElement;
    root.setAttribute('lang', lang);
    root.setAttribute('translate', 'no');
  }, [i18n.language]);
  return null;
};

const LayoutWrapper: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [/* showChat */, setShowChat] = useState(false); // 将来的にチャットウィジェットを復活させる場合のために保持
  // IP 制限は管理画面で無効化中のため常時 true（再有効化する場合は useState + /check-ip 呼び出しに戻す）
  const ipAllowed = true;

  // ===== ルーティング領域の判定（ドクター画面 / 管理画面 / マイページかどうか） =====
  const isDoctorArea = location.pathname.startsWith('/doctor');
  const isAdminArea = location.pathname.startsWith('/admin');
  const isMypageArea = location.pathname.startsWith('/mypage');

  // 認証系ページ（ログイン・会員登録・パスワードリセット等）かどうか
  const isAuthPage = useMemo(() => {
    const p = location.pathname;
    return (
      p.startsWith('/login') ||          // 一般ユーザー ログイン
      p.startsWith('/register') ||       // 一般ユーザー 新規登録
      p.startsWith('/reset') ||          // パスワードリセット
      p.startsWith('/forgot') ||         // パスワードを忘れた場合
      p.startsWith('/doctor/login') ||   // ドクター用ログイン
      p.startsWith('/admin/login') ||    // 管理者用ログイン
      p.includes('/auth')                // その他 auth 系パス
    );
  }, [location.pathname]);

  // 一般ユーザー領域 かつ 認証ページ以外 かつ IP 許可 の場合にのみチャットを表示（現在はコメントアウト中）
  const shouldShowChat = !isDoctorArea && !isAdminArea && !isAuthPage && ipAllowed;

  // const shouldShowWeChat = false; // ⛔️ WeChat フロートを非表示

  return (
    <>
      <ScrollToTop />
      <HtmlLangSync />

      {/* ルーティング（Navbar などは内部で ipAllowed を受け取る） */}
      <AppRoutes ipAllowed={ipAllowed} />

      {/* ⛔️ チャットウィジェット（一時停止中） */}
      {/* {shouldShowChat && (
        <ChatBotWidget show={showChat} onToggle={() => setShowChat((v) => !v)} />
      )} */}

      {/* ✅ ページ先頭へ戻るボタン（LP ページ・LP ドメインの全パスでは非表示） */}
      {(() => {
        const isLpDomain = window.location.hostname === 'lp.smhc.ai' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const lpInterceptedPaths = ['/', '/login', '/login_success', '/register', '/signup', '/forgot', '/reset-password'];
        const isLpRoute = location.pathname === '/lp' || (isLpDomain && lpInterceptedPaths.includes(location.pathname));
        return !isLpRoute ? <ScrollTopButton /> : null;
      })()}

      {/* ⛔️ WeChat グループ QR コード（非表示） */}
      {/* {shouldShowWeChat && <WeChatGroupFloat />} */}

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        {/* ✅ グローバル修正コンポーネント：幽霊テキストや外部チャットボタンを非表示にする */}
        <GlobalFixes />
        <AuthProvider>
          <LayoutWrapper />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
